import { useState, useCallback, useRef, useMemo, useEffect } from "react";
import type { TileConfig, FontPresetId, DashboardTileLayout, Preset, SavedConfig } from "../types";
import type { WidgetId } from "@/other/constants/EntityManagerConfig";
import { createDefaultPresets, LAYOUT_PRESET_TEMPLATES } from "../constants";
import { ENTITY_CONFIGS, type EntityTypeKey } from "@/other/constants/EntityManagerConfig";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useConfirm } from "@omit/react-confirm-dialog";
import { Button } from "@/modern/components/ui/button-shadcn";
import { verticalCompactor } from "react-grid-layout";
import type { LayoutItem } from "react-grid-layout";

/**
 * Backfill x/y for visible tiles that don't yet have positions (legacy
 * persisted configs). Hidden tiles never need positions. Running this in
 * `mergeWithWidgetConfig` ensures the configurator's tile state and the
 * persisted preset snapshot agree on positions, so `hasDraftChanges` only
 * flips when the user actually edits something.
 *
 * Uses react-grid-layout's own `verticalCompactor.compact()` to resolve the
 * initial placement — no custom grid algorithm required.
 */
function backfillPositions(tiles: TileConfig[], columns: number): TileConfig[] {
  const visibleIndices: number[] = [];
  for (let i = 0; i < tiles.length; i++) {
    if (tiles[i].visible) visibleIndices.push(i);
  }
  const needsBackfill = visibleIndices.some(
    (i) => tiles[i].x === undefined || tiles[i].y === undefined
  );
  if (!needsBackfill) return tiles;

  const visibleTiles = visibleIndices.map((i) => tiles[i]);

  // Bottom edge of all already-positioned tiles — unpositioned tiles start below.
  const maxY = visibleTiles.reduce(
    (max, t) => (t.x !== undefined ? Math.max(max, (t.y ?? 0) + t.rowSpan) : max),
    0
  );

  // Build LayoutItems for the library compactor.
  // Positioned tiles are marked static so the compactor doesn't move them.
  // Unpositioned tiles are assigned sequential x positions below the grid.
  let curX = 0, curY = maxY, rowH = 0;
  const items: LayoutItem[] = visibleTiles.map((tile) => {
    const w = Math.min(tile.colSpan, columns);
    const h = Math.max(1, tile.rowSpan);
    if (tile.x !== undefined && tile.y !== undefined) {
      return { i: tile.id, x: tile.x, y: tile.y, w, h, static: true };
    }
    if (curX + w > columns) {
      curY += rowH;
      curX = 0;
      rowH = 0;
    }
    const item: LayoutItem = { i: tile.id, x: curX, y: curY, w, h };
    curX += w;
    rowH = Math.max(rowH, h);
    return item;
  });

  // Let the library compact upward — identical algorithm used internally by
  // react-grid-layout during every drag/resize interaction.
  const compacted = verticalCompactor.compact(items, columns);
  const posMap = new Map(compacted.map((it) => [it.i, { x: it.x, y: it.y }]));

  const next = tiles.slice();
  visibleIndices.forEach((tileIdx) => {
    const tile = tiles[tileIdx];
    if (tile.x === undefined || tile.y === undefined) {
      const pos = posMap.get(tile.id);
      if (pos) next[tileIdx] = { ...next[tileIdx], x: pos.x, y: pos.y };
    }
  });
  return next;
}

/**
 * When RGL reports a layout width, keep the tile's stored colSpan if the report
 * is only the column clamp (display width) and not a user resize.
 */
function resolveColSpanFromLayout(
  tile: TileConfig,
  layoutW: number,
  columns: number
): number {
  const clampedW = Math.min(tile.colSpan, columns);
  if (layoutW < clampedW && layoutW < tile.colSpan) {
    return layoutW;
  }
  if (layoutW === clampedW && layoutW < tile.colSpan) {
    return tile.colSpan;
  }
  return layoutW;
}

/**
 * Recompact all visible tiles when the column count changes.
 * Preserves visual order (y, then x) so a 1-column stack reflows horizontally
 * when columns increase. Only updates positions — colSpan/rowSpan are kept so
 * widths restore when columns increase again.
 */
function recompactVisibleTiles(tiles: TileConfig[], columns: number): TileConfig[] {
  const visible = tiles.filter((t) => t.visible);
  if (visible.length === 0) return tiles;

  const sorted = [...visible].sort(
    (a, b) => (a.y ?? 0) - (b.y ?? 0) || (a.x ?? 0) - (b.x ?? 0)
  );

  let curX = 0;
  let curY = 0;
  let rowH = 0;
  const items: LayoutItem[] = sorted.map((tile) => {
    const w = Math.min(tile.colSpan, columns);
    const h = Math.max(1, tile.rowSpan);
    if (curX + w > columns) {
      curY += rowH;
      curX = 0;
      rowH = 0;
    }
    const item: LayoutItem = { i: tile.id, x: curX, y: curY, w, h };
    curX += w;
    rowH = Math.max(rowH, h);
    return item;
  });

  const compacted = verticalCompactor.compact(items, columns);
  const layoutById = new Map(compacted.map((it) => [it.i, it]));

  return tiles.map((tile) => {
    if (!tile.visible) return tile;
    const item = layoutById.get(tile.id);
    if (!item) return tile;
    return {
      ...tile,
      x: item.x,
      y: item.y,
    };
  });
}

function mergeWithWidgetConfig(
  savedTiles: DashboardTileLayout[],
  entityType: EntityTypeKey,
  columns: number
): TileConfig[] {
  const config = ENTITY_CONFIGS[entityType];
  if (!config) return [];

  const widgetMap = new Map(config.widgets.map((w) => [w.id, w]));
  const merged: TileConfig[] = [];

  for (const saved of savedTiles) {
    const widget = widgetMap.get(saved.id);
    if (!widget) continue;
    merged.push({
      id: saved.id,
      title: widget.title,
      color: widget.color,
      colSpan: saved.colSpan,
      rowSpan: saved.rowSpan,
      visible: saved.visible,
      x: saved.x,
      y: saved.y,
    });
    widgetMap.delete(saved.id);
  }

  for (const widget of widgetMap.values()) {
    merged.push({
      id: widget.id,
      title: widget.title,
      color: widget.color,
      colSpan: widget.defaultColSpan,
      rowSpan: widget.defaultRowSpan,
      visible: false,
    });
  }

  return backfillPositions(merged, columns);
}

function getStorageKey(entityType: EntityTypeKey): string {
  return `${PERSISTENCE_STATE_KEYS.dashboardConfig}-${entityType}`;
}

/** Resolve tiles/columns/fontPreset for a given preset ID. */
function resolvePreset(
  presetId: string,
  presets: Preset[]
): { tiles: DashboardTileLayout[]; columns: number; fontPreset: FontPresetId } {
  const preset = presets.find((p) => p.id === presetId);
  if (preset) {
    return {
      tiles: preset.tiles,
      columns: preset.columns,
      fontPreset: preset.fontPreset ?? "standard",
    };
  }

  // Fallback to first preset
  const fallback = presets[0];
  if (fallback) {
    return {
      tiles: fallback.tiles,
      columns: fallback.columns,
      fontPreset: fallback.fontPreset ?? "standard",
    };
  }

  // Ultimate fallback (should never happen)
  return { tiles: [], columns: 4, fontPreset: "standard" };
}

/**
 * Syncs saved default presets with current templates.
 * - Adds new default presets if a template was added
 * - Removes default presets if their template was removed
 * - Keeps user modifications to existing default presets (including renames)
 * - Uses templateName for matching (falls back to name for backwards compatibility)
 */
function syncDefaultPresetsWithTemplates(savedPresets: Preset[]): Preset[] {
  const templateNames = new Set(LAYOUT_PRESET_TEMPLATES.map(t => t.name));
  const savedDefaultPresets = savedPresets.filter(p => p.isDefault);
  // Use templateName for matching (fall back to name for old presets without templateName)
  const savedTemplateNames = new Set(savedDefaultPresets.map(p => p.templateName ?? p.name));
  const customPresets = savedPresets.filter(p => !p.isDefault);

  // Remove default presets whose template no longer exists (match by templateName)
  const filteredDefaults = savedDefaultPresets
    .filter(p => templateNames.has(p.templateName ?? p.name))
    .map(p => {
      // Ensure templateName is set for backwards compatibility
      if (!p.templateName) {
        return { ...p, templateName: p.name };
      }
      return p;
    });

  // Add new default presets for templates that don't have a saved preset
  const newDefaults: Preset[] = [];
  for (const template of LAYOUT_PRESET_TEMPLATES) {
    if (!savedTemplateNames.has(template.name)) {
      newDefaults.push({
        id: crypto.randomUUID(),
        name: template.name,
        templateName: template.name,
        columns: template.columns,
        fontPreset: template.fontPreset,
        isDefault: true,
        tiles: template.tiles.map(t => ({ ...t })),
      });
    }
  }

  // Combine: existing defaults (synced) + new defaults + custom presets
  return [...filteredDefaults, ...newDefaults, ...customPresets];
}

/**
 * Syncs a preset's tiles with current widget configuration.
 * - Removes tiles whose widgets no longer exist
 * - Adds new widgets as hidden tiles with default sizes
 * Returns the updated preset (or original if no changes needed)
 */
function syncPresetTilesWithWidgets(preset: Preset, entityType: EntityTypeKey): Preset {
  const config = ENTITY_CONFIGS[entityType];
  if (!config) return preset;

  const availableWidgetIds = new Set(config.widgets.map(w => w.id));

  // Remove tiles that no longer exist in widget config
  const filteredTiles = preset.tiles.filter(t => availableWidgetIds.has(t.id));
  const existingTileIds = new Set(filteredTiles.map(t => t.id));

  // Add new widgets as hidden tiles
  const newTiles: DashboardTileLayout[] = config.widgets
    .filter(w => !existingTileIds.has(w.id))
    .map(w => ({
      id: w.id,
      colSpan: w.defaultColSpan,
      rowSpan: w.defaultRowSpan,
      visible: false,
    }));

  // Only return new preset if there were changes
  if (filteredTiles.length === preset.tiles.length && newTiles.length === 0) {
    return preset;
  }

  return {
    ...preset,
    tiles: [...filteredTiles, ...newTiles],
  };
}

/**
 * Syncs all presets' tiles with current widget configuration.
 */
function syncAllPresetTilesWithWidgets(presets: Preset[], entityType: EntityTypeKey): Preset[] {
  return presets.map(preset => syncPresetTilesWithWidgets(preset, entityType));
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function loadSavedConfig(storageKey: string, userPreferences: any, entityType: EntityTypeKey): SavedConfig {
  try {
    const parsed = JSON.parse(userPreferences?.getSetting(storageKey) ?? "null");
    if (!parsed || !Array.isArray(parsed.presets) || parsed.presets.length === 0) {
      // First load: create default presets from templates and sync with widgets
      const defaultPresets = createDefaultPresets();
      const syncedPresets = syncAllPresetTilesWithWidgets(defaultPresets, entityType);
      return {
        activePresetId: syncedPresets[0].id,
        presets: syncedPresets,
      };
    }

    // Sync default presets with current templates (add new, remove deleted)
    let syncedPresets = syncDefaultPresetsWithTemplates(parsed.presets);

    // Sync all presets' tiles with current widget configuration
    syncedPresets = syncAllPresetTilesWithWidgets(syncedPresets, entityType);

    // If active preset was removed, fall back to first preset
    const activePresetExists = syncedPresets.some(p => p.id === parsed.activePresetId);
    const activePresetId = activePresetExists ? parsed.activePresetId : syncedPresets[0]?.id;

    return {
      activePresetId: activePresetId ?? syncedPresets[0]?.id,
      presets: syncedPresets,
    };
  } catch {
    const defaultPresets = createDefaultPresets();
    const syncedPresets = syncAllPresetTilesWithWidgets(defaultPresets, entityType);
    return { activePresetId: syncedPresets[0].id, presets: syncedPresets };
  }
}

export function useDashboardConfigurator(entityType: EntityTypeKey) {
  const { userPreferences } = useSwivuBryntumContext();
  const storageKey = getStorageKey(entityType);

  const initialConfig = useRef(loadSavedConfig(storageKey, userPreferences, entityType));

  // Persist synced config if templates or widgets changed (runs once on mount)
  const hasPersisted = useRef(false);
  useEffect(() => {
    if (hasPersisted.current) return;
    hasPersisted.current = true;

    // Check if sync made changes by comparing with raw stored data
    try {
      const raw = JSON.parse(userPreferences?.getSetting(storageKey) ?? "null");
      if (raw && Array.isArray(raw.presets)) {
        // Deep compare to detect any sync changes (templates or tiles)
        const rawJson = JSON.stringify(raw);
        const syncedJson = JSON.stringify(initialConfig.current);

        if (rawJson !== syncedJson) {
          // Sync made changes, persist the updated config
          userPreferences?.updateSetting(storageKey, JSON.stringify(initialConfig.current));
        }
      } else {
        // No existing data, persist the initial config
        userPreferences?.updateSetting(storageKey, JSON.stringify(initialConfig.current));
      }
    } catch {
      // Parsing error - persist the synced config
      userPreferences?.updateSetting(storageKey, JSON.stringify(initialConfig.current));
    }
  }, [userPreferences, storageKey]);

  // Persisted state
  const [savedConfig, setSavedConfig] = useState<SavedConfig>(initialConfig.current);

  // Draft state (what the configurator edits)
  const [presets, setPresets] = useState<Preset[]>(initialConfig.current.presets);
  const [activePresetId, setActivePresetId] = useState<string>(initialConfig.current.activePresetId);

  const initialResolved = resolvePreset(initialConfig.current.activePresetId, initialConfig.current.presets);
  const [tiles, setTiles] = useState<TileConfig[]>(
    () => mergeWithWidgetConfig(initialResolved.tiles, entityType, initialResolved.columns)
  );
  const [columns, setColumns] = useState<number>(initialResolved.columns);
  const [activeFontPreset, setActiveFontPreset] = useState<FontPresetId>(initialResolved.fontPreset);

  // Saved-state derived values for the dashboard view
  const savedResolved = useMemo(
    () => resolvePreset(savedConfig.activePresetId, savedConfig.presets),
    [savedConfig]
  );
  const savedTiles = useMemo(
    () => mergeWithWidgetConfig(savedResolved.tiles, entityType, savedResolved.columns),
    [savedResolved.tiles, savedResolved.columns, entityType]
  );
  const savedVisibleTiles = useMemo(() => savedTiles.filter((t) => t.visible), [savedTiles]);
  const savedColumns = savedResolved.columns;
  const savedFontPreset = savedResolved.fontPreset;
  const isWarRoom = savedFontPreset === "war-room";

  useEffect(() => {
    if (!isWarRoom) return;

    const style = document.createElement("style");
    style.setAttribute("data-war-room-zoom", "");
    style.textContent = `body > [id*="radix"]:not([data-vaul-drawer]) { zoom: 1.15; }`;
    document.head.appendChild(style);

    return () => style.remove();
  }, [isWarRoom]);

  // Draft-state derived values
  const visibleTiles = useMemo(() => tiles.filter((t) => t.visible), [tiles]);
  const hiddenTiles = useMemo(() => tiles.filter((t) => !t.visible), [tiles]);

  // Get active preset info
  const activePreset = useMemo(() => presets.find((p) => p.id === activePresetId), [presets, activePresetId]);
  const isActivePresetDefault = activePreset?.isDefault ?? false;

  // Derived presets for UI grouping
  const defaultPresets = useMemo(() => presets.filter((p) => p.isDefault), [presets]);
  const customPresets = useMemo(() => presets.filter((p) => !p.isDefault), [presets]);

  /** Snapshot of what the currently selected preset looks like (for dirty comparison). */
  const activePresetSnapshot = useMemo(() => {
    return resolvePreset(activePresetId, presets);
  }, [activePresetId, presets]);

  /** Whether the draft tiles/columns/font/name differ from the selected preset's stored values. */
  const hasDraftChanges = useMemo(() => {
    const draftTiles = JSON.stringify(
      tiles.map((t) => ({ id: t.id, colSpan: t.colSpan, rowSpan: t.rowSpan, visible: t.visible, x: t.x, y: t.y }))
    );
    const presetTiles = JSON.stringify(
      mergeWithWidgetConfig(activePresetSnapshot.tiles, entityType, activePresetSnapshot.columns)
        .map((t) => ({ id: t.id, colSpan: t.colSpan, rowSpan: t.rowSpan, visible: t.visible, x: t.x, y: t.y }))
    );
    const currentPreset = presets.find((p) => p.id === activePresetId);
    const savedPreset = savedConfig.presets.find((p) => p.id === activePresetId);
    const nameChanged = !!(currentPreset && savedPreset && currentPreset.name !== savedPreset.name);
    return (
      draftTiles !== presetTiles ||
      columns !== activePresetSnapshot.columns ||
      activeFontPreset !== activePresetSnapshot.fontPreset ||
      nameChanged
    );
  }, [tiles, columns, activeFontPreset, activePresetSnapshot, entityType, activePresetId, presets, savedConfig]);

  const isActivePresetApplied = activePresetId === savedConfig.activePresetId;
  const isAlreadyApplied = isActivePresetApplied && !hasDraftChanges;
  const showApplyButton = !isAlreadyApplied && !hasDraftChanges;

  // Keep isDirty for close-confirmation (any deviation from saved state, including preset names)
  const isDirty = useMemo(() => {
    const draftSnapshot = JSON.stringify({
      tiles: tiles.map((t) => ({ id: t.id, colSpan: t.colSpan, rowSpan: t.rowSpan, visible: t.visible, x: t.x, y: t.y })),
      columns,
      fontPreset: activeFontPreset,
      activePresetId,
      presetNames: presets.map((p) => ({ id: p.id, name: p.name })),
    });
    const savedSnapshot = JSON.stringify({
      ...resolvePreset(savedConfig.activePresetId, savedConfig.presets),
      activePresetId: savedConfig.activePresetId,
      presetNames: savedConfig.presets.map((p) => ({ id: p.id, name: p.name })),
    });
    return draftSnapshot !== savedSnapshot;
  }, [tiles, columns, activeFontPreset, activePresetId, savedConfig, presets]);

  // --- Tile editing handlers ---

  /**
   * Apply a react-grid-layout layout update for the visible tiles. Hidden
   * tiles keep their state and remain at the end of the array.
   *
   * Each entry: `{ i: tileId, x, y, w, h }` (RGL `LayoutItem` shape).
   */
  const handleLayoutChange = useCallback(
    (
      layout: ReadonlyArray<{
        i: string;
        x: number;
        y: number;
        w: number;
        h: number;
      }>
    ) => {
      setTiles((prev) => {
        const byId = new Map(layout.map((item) => [item.i, item]));
        let changed = false;
        const next = prev.map((tile) => {
          const item = byId.get(tile.id);
          if (!item) return tile;
          const colSpan = resolveColSpanFromLayout(tile, item.w, columns);
          if (
            tile.x === item.x &&
            tile.y === item.y &&
            tile.colSpan === colSpan &&
            tile.rowSpan === item.h
          ) {
            return tile;
          }
          changed = true;
          return {
            ...tile,
            x: item.x,
            y: item.y,
            colSpan,
            rowSpan: item.h,
          };
        });
        return changed ? next : prev;
      });
    },
    [columns]
  );

  const handleToggleVisibility = useCallback((id: WidgetId) => {
    setTiles((prev) =>
      prev.map((t) =>
        t.id === id
          ? t.visible
            ? { ...t, visible: false, x: undefined, y: undefined }
            : { ...t, visible: true }
          : t
      )
    );
  }, []);

  const handleAddTile = useCallback((id: WidgetId) => {
    setTiles((prev) =>
      prev.map((t) => (t.id === id ? { ...t, visible: true } : t))
    );
  }, []);

  /**
   * Move a tile between visible/hidden containers. When `makeVisible` is true
   * and `position` is provided, the tile is pinned to (x, y) so the configurator
   * can drop it at the cursor's grid cell.
   */
  const handleMoveBetweenContainers = useCallback(
    (
      id: WidgetId,
      makeVisible: boolean,
      position?: { x: number; y: number }
    ) => {
      setTiles((prev) =>
        prev.map((t) => {
          if (t.id !== id) return t;
          if (makeVisible) {
            return {
              ...t,
              visible: true,
              x: position?.x ?? t.x,
              y: position?.y ?? t.y,
            };
          }
          return { ...t, visible: false, x: undefined, y: undefined };
        })
      );
    },
    []
  );

  const handleColumnChange = useCallback((count: number) => {
    setColumns(count);
    setTiles((prev) => recompactVisibleTiles(prev, count));
  }, []);

  const handleFontPresetChange = useCallback((presetId: FontPresetId) => {
    setActiveFontPreset(presetId);
  }, []);

  const handleLayoutPresetChange = useCallback((presetId: string) => {
    const resolved = resolvePreset(presetId, presets);
    setActivePresetId(presetId);
    setColumns(resolved.columns);
    setTiles(mergeWithWidgetConfig(resolved.tiles, entityType, resolved.columns));
    setActiveFontPreset(resolved.fontPreset);
  }, [presets, entityType]);

  // --- Persistence helpers ---

  const persist = useCallback((config: SavedConfig) => {
    userPreferences?.updateSetting(storageKey, JSON.stringify(config));
    setSavedConfig(config);
  }, [userPreferences, storageKey]);

  const [isSaving, setIsSaving] = useState(false);

  const withSavingIndicator = useCallback((fn: () => void) => {
    setIsSaving(true);
    fn();
    setTimeout(() => setIsSaving(false), 800);
  }, []);

  // --- Preset actions ---

  const handleApplyPreset = useCallback(() => {
    withSavingIndicator(() => {
      persist({ activePresetId, presets });
    });
  }, [activePresetId, presets, persist, withSavingIndicator]);

  const handleSaveAsNewPreset = useCallback((name: string) => {
    withSavingIndicator(() => {
      const newPreset: Preset = {
        id: crypto.randomUUID(),
        name,
        columns,
        fontPreset: activeFontPreset,
        isDefault: false,
        tiles: tiles.map((t) => ({
          id: t.id,
          colSpan: t.colSpan,
          rowSpan: t.rowSpan,
          visible: t.visible,
          x: t.x,
          y: t.y,
        })),
      };
      const updatedPresets = [...presets, newPreset];
      setPresets(updatedPresets);
      setActivePresetId(newPreset.id);
      persist({ activePresetId: newPreset.id, presets: updatedPresets });
    });
  }, [columns, activeFontPreset, tiles, presets, persist, withSavingIndicator]);

  /** Unified handler for updating any preset (default or custom) */
  const handleUpdatePreset = useCallback((name?: string) => {
    withSavingIndicator(() => {
      const updatedPresets = presets.map((p) =>
        p.id === activePresetId
          ? {
              ...p,
              ...(name !== undefined ? { name } : {}),
              columns,
              fontPreset: activeFontPreset,
              tiles: tiles.map((t) => ({
                id: t.id,
                colSpan: t.colSpan,
                rowSpan: t.rowSpan,
                visible: t.visible,
                x: t.x,
                y: t.y,
              })),
            }
          : p
      );
      setPresets(updatedPresets);
      persist({ activePresetId, presets: updatedPresets });
    });
  }, [activePresetId, columns, activeFontPreset, tiles, presets, persist, withSavingIndicator]);

  const handleDeletePreset = useCallback((id: string) => {
    const preset = presets.find((p) => p.id === id);
    // Cannot delete default presets
    if (preset?.isDefault) return;

    const updatedPresets = presets.filter((p) => p.id !== id);
    const needsFallback = savedConfig.activePresetId === id;
    const fallbackPresetId = updatedPresets[0]?.id;

    if (!fallbackPresetId) return; // Safety check

    setPresets(updatedPresets);

    if (needsFallback || activePresetId === id) {
      const resolved = resolvePreset(fallbackPresetId, updatedPresets);
      setActivePresetId(fallbackPresetId);
      setColumns(resolved.columns);
      setTiles(mergeWithWidgetConfig(resolved.tiles, entityType, resolved.columns));
      setActiveFontPreset(resolved.fontPreset);
    }

    persist({
      activePresetId: needsFallback ? fallbackPresetId : savedConfig.activePresetId,
      presets: updatedPresets,
    });
  }, [presets, savedConfig, activePresetId, entityType, persist]);

  const handleRenamePreset = useCallback((id: string, name: string) => {
    setPresets((prev) => prev.map((p) => (p.id === id ? { ...p, name } : p)));
  }, []);

  const handleQuickLayoutPresetChange = useCallback((presetId: string) => {
    const resolved = resolvePreset(presetId, presets);
    setActivePresetId(presetId);
    setColumns(resolved.columns);
    setTiles(mergeWithWidgetConfig(resolved.tiles, entityType, resolved.columns));
    setActiveFontPreset(resolved.fontPreset);
    persist({ activePresetId: presetId, presets });
  }, [presets, entityType, persist]);

  // --- Discard / close ---

  const handleDiscard = useCallback(() => {
    const resolved = resolvePreset(savedConfig.activePresetId, savedConfig.presets);
    setTiles(mergeWithWidgetConfig(resolved.tiles, entityType, resolved.columns));
    setColumns(resolved.columns);
    setActiveFontPreset(resolved.fontPreset);
    setActivePresetId(savedConfig.activePresetId);
    setPresets(savedConfig.presets);
  }, [savedConfig, entityType]);

  const confirm = useConfirm();

  const handleCloseConfigurator = useCallback(
    async (closeModal: () => void) => {
      if (!isDirty) {
        closeModal();
        return;
      }

      const confirmed = await confirm({
        title: "Unsaved Changes",
        description: "You have unsaved changes. What would you like to do?",
        customActions: ({ confirm: confirmAction, cancel }: { confirm: () => void; cancel: () => void }) => (
          <div className="flex w-full justify-end gap-3">
            <Button variant="ghost" onClick={cancel} size="sm">Keep working</Button>
            <Button variant="destructive" onClick={() => { handleDiscard(); confirmAction(); }} size="sm">Discard</Button>
          </div>
        ),
      });

      if (confirmed) closeModal();
    },
    [isDirty, confirm, handleDiscard]
  );

  return {
    // Draft state (for configurator)
    tiles,
    columns,
    activeFontPreset,
    activePresetId,
    presets,
    defaultPresets,
    customPresets,
    activePreset,
    isActivePresetDefault,
    visibleTiles,
    hiddenTiles,
    isDirty,
    hasDraftChanges,
    isActivePresetApplied,
    isAlreadyApplied,
    showApplyButton,
    handleLayoutChange,
    handleToggleVisibility,
    handleAddTile,
    handleMoveBetweenContainers,
    handleColumnChange,
    handleFontPresetChange,
    handleLayoutPresetChange,
    handleApplyPreset,
    handleSaveAsNewPreset,
    handleUpdatePreset,
    handleDeletePreset,
    handleRenamePreset,
    handleDiscard,
    handleCloseConfigurator,
    handleQuickLayoutPresetChange,
    // Saved state (for dashboard view)
    savedVisibleTiles,
    savedColumns,
    savedFontPreset,
    isSaving,
  };
}
